import React, { useId, useMemo, useRef, useState } from 'react';
import {
  CircleNotchIcon,
  DownloadSimpleIcon,
  PaperclipIcon,
  TrashIcon,
  UploadSimpleIcon,
  WarningCircleIcon,
} from '@/components/icons';
import accept from 'attr-accept';
import { useI18n } from '@/i18n/I18nProvider';
import { useDownloadAttachment } from '@/lib/hooks/useDownloadAttachment';

export interface DocumentT {
  id?: number | null;
  documentTypeId?: number | null;
  name?: string | null;
  url?: string | null;
  file?: File | null;
  status?: number | null;
  comment?: string | null;
}

interface DocumentType {
  id: number;
  name: string;
  description?: string;
  required?: boolean;
  acceptedFormats?: string;
  maxFileSize?: number;
}

interface DocumentPickerProps {
  documentType: DocumentType;
  lastUsed: DocumentT | null;
  submitted: DocumentT | null;
  onChange: (file: DocumentT | null) => void;
  isReadOnly?: boolean;
  renderStatus?: () => React.ReactNode;
}

const DefaultAcceptedFileFormats = '.pdf,.jpg,.jpeg';
const DefaultMaxFileSize = 2097152;

export default function DocumentPicker({
  documentType,
  lastUsed,
  submitted,
  onChange,
  isReadOnly,
  renderStatus,
}: DocumentPickerProps) {
  const { t } = useI18n();
  const inputId = useId();
  const { downloadAttachment, isDownloading } = useDownloadAttachment();
  const [selectedDocument, setSelectedDocument] = useState<DocumentT | null>(submitted);
  const [showPreviousPrompt, setShowPreviousPrompt] = useState<boolean>(!!(!submitted && lastUsed));
  const [fileSizeError, setFileSizeError] = useState<string | null>(null);
  const [fileTypeError, setFileTypeError] = useState<string | null>(null);
  const fileInputRef = useRef<HTMLInputElement>(null);

  const handleFileChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    const acceptedFileFormats = documentType?.acceptedFormats ?? DefaultAcceptedFileFormats;
    const maxFileSize = documentType?.maxFileSize ?? DefaultMaxFileSize;
    if (file) {
      if (!accept(file, acceptedFileFormats)) {
        const formattedTypes = acceptedFileFormats?.replaceAll('.', '').replaceAll(',', ', ').toUpperCase();
        const errorMsg = t.fileTypeInvalidError?.replace('{formats}', formattedTypes);
        setFileTypeError(errorMsg);
        setFileSizeError(null);
        if (fileInputRef.current) {
          fileInputRef.current.value = '';
        }
        return;
      }
      setFileTypeError(null);

      if (maxFileSize && file.size > maxFileSize) {
        const maxMb = Math.round(maxFileSize / (1024 * 1024));
        const errorMsg = t.fileSizeExceededError?.replace('{maxMb}', String(maxMb));
        setFileSizeError(errorMsg);
        if (fileInputRef.current) {
          fileInputRef.current.value = '';
        }
        return;
      }
      setFileSizeError(null);

      setSelectedDocument({ documentTypeId: documentType?.id, file });
      setShowPreviousPrompt(false); // Hide the prompt if a new file is uploaded

      onChange?.({ documentTypeId: documentType?.id, file });
    }
  };

  const handleRemoveFile = () => {
    setFileSizeError(null);
    setFileTypeError(null);
    setSelectedDocument(null);
    if (fileInputRef.current) {
      fileInputRef.current.value = '';
    }
    onChange?.(null);
  };

  const handleUseLastDocument = () => {
    if (lastUsed) {
      setSelectedDocument(lastUsed);
      setShowPreviousPrompt(false);
      onChange?.(lastUsed);
    }
  };

  const handleDismissPrevious = () => {
    setShowPreviousPrompt(false);
  };

  return (
    <div className="mb-8">
      <div className="grid grid-cols-1 lg:grid-cols-[420px_1fr] gap-4 lg:gap-8 items-start">
        {/* Left Side: Label & Description */}
        <div className="pt-2">
          <label className="text-gray-800 text-base block font-semibold">
            {documentType.name}
            {documentType.required && <span className="text-aered-600 ml-1">*</span>}
          </label>
          {documentType.description && <p className="text-gray-600 text-sm mt-1">{documentType.description}</p>}
        </div>

        {/* Right Side: Interactive Controls */}
        <div>
          {/* STATE 2: File Already Selected */}
          {selectedDocument && (
            <div className="flex items-center justify-between bg-gray-50 border border-gray-200 transition-colors rounded-lg p-3 mb-4 max-w-100">
              <div className="flex items-center gap-3 overflow-hidden">
                <PaperclipIcon />
                <span className="text-gray-900 text-sm truncate">
                  {selectedDocument?.file?.name ?? selectedDocument?.name}
                </span>
              </div>
              <div className="flex items-center gap-3 shrink-0 pl-4">
                {selectedDocument?.url && (
                  <button
                    type="button"
                    disabled={isDownloading}
                    onClick={() => downloadAttachment(selectedDocument.url!)}
                    className="text-yellow-600 hover:text-yellow-700 disabled:opacity-50"
                    aria-label={t.downloadFile}
                  >
                    {isDownloading ? <CircleNotchIcon className="animate-spin" /> : <DownloadSimpleIcon />}
                  </button>
                )}

                {/* Remove Button with Tooltip */}
                {!isReadOnly && (<div className="relative group flex items-center justify-center">
                  <div className="absolute bottom-full mb-2 hidden group-hover:block w-max bg-gray-900 text-white text-xs px-2 py-1 rounded shadow-sm before:content-[''] before:absolute before:top-full before:left-1/2 before:-translate-x-1/2 before:border-4 before:border-transparent before:border-t-gray-900">
                    {t.removeFile}
                  </div>
                  <button
                    type="button"
                    onClick={handleRemoveFile}
                    className="bg-[#D32F2F] hover:bg-aered-700 text-white p-2 rounded transition-colors"
                    aria-label={t.removeFile}
                  >
                    <TrashIcon />
                  </button>
                </div>)}
              </div>
            </div>
          )}

          {/* aegov file input control */}
          {!isReadOnly && (<>
            <div className="aegov-form-control aegov-file-input-control mb-2">
              <label htmlFor={inputId} className="file-input-label aegov-btn btn-secondary btn-sm text-sm">
                <UploadSimpleIcon />
                {t.uploadFile}
              </label>
              <input
                type="file"
                name={inputId}
                id={inputId}
                ref={fileInputRef}
                onChange={handleFileChange}
                accept={documentType.acceptedFormats || DefaultAcceptedFileFormats}
              />
            </div>

            <p className="text-gray-600 text-sm max-w-md leading-relaxed">
              {t.formatLabel}{' '}
              {(documentType.acceptedFormats || DefaultAcceptedFileFormats)
                .replaceAll('.', '')
                .replaceAll(',', ', ')
                .toUpperCase()}{' '}
              {t.maxFileSizeLabel} {Math.floor((documentType.maxFileSize || DefaultMaxFileSize) / 1024 / 1024)}MB
            </p>

            {fileSizeError && <p className="text-red-600 text-sm font-medium mt-1">{fileSizeError}</p>}

            {fileTypeError && <p className="text-red-600 text-sm font-medium mt-1">{fileTypeError}</p>}
          </>)}

          {/* Document Status */}
          {renderStatus?.()}

          {/* STATE 3: Previous Document Prompt */}
          {!isReadOnly && showPreviousPrompt && (
            <div className="bg-gray-100 rounded-lg p-5 mt-4 w-full max-w-lg">
              <div className="flex gap-3 mb-5">
                <div className="shrink-0 mt-0.5">
                  <WarningCircleIcon />
                </div>
                <p className="text-sm text-gray-800 leading-relaxed">
                  {t.previousDocumentPrompt}{' '}
                  {lastUsed?.url ? (
                    <button
                      type="button"
                      disabled={isDownloading}
                      onClick={() => downloadAttachment(lastUsed.url!)}
                      className="font-bold text-gray-900 hover:underline disabled:opacity-50"
                    >
                      {t.viewDocument}
                    </button>
                  ) : null}
                </p>
              </div>

              <div className="flex gap-3">
                <button
                  type="button"
                  onClick={handleUseLastDocument}
                  className="aegov-btn btn-sm bg-aegreen-600 hover:bg-aegreen-700 atext-white text-sm"
                >
                  {t.useDocument}
                </button>
                <button
                  type="button"
                  onClick={handleDismissPrevious}
                  className="aegov-btn btn-sm bg-white border border-gray-300 hover:bg-gray-50 text-gray-800 text-sm"
                >
                  {t.noThanks}
                </button>
              </div>
            </div>
          )}
        </div>
      </div>
    </div>
  );
}
